/**
 * Privacy-safe label for a searched address. Strips the house number and any
 * apartment/unit prefix so the stored display never reveals a doorstep, while
 * keeping enough (street + city) to recognize the search.
 *
 * "350 5th Ave, New York, NY 10118" → "5th Ave, New York"
 */

const UNIT_PREFIX_RE = /^\s*(apt|apartment|unit|suite|ste|#)\.?\s*\S+\s*/i;
const LEADING_HOUSE_NUMBER_RE = /^\s*\d+[A-Za-z-]*\s+/;
const STATE_ZIP_RE = /\s+[A-Z]{2}(\s+\d{5}(-\d{4})?)?\s*$/;
const MAX_LABEL_LEN = 60;

/** Drop a leading house number and apartment/unit prefix from a street line. */
export function stripHouseNumber(streetLine: string): string {
  return streetLine
    .trim()
    .replace(UNIT_PREFIX_RE, '')
    .replace(LEADING_HOUSE_NUMBER_RE, '')
    .trim();
}

/** Sanitize a full address to "street, city". Returns '' when nothing usable. */
export function sanitizeAddress(address: string | null | undefined): string {
  if (!address) return '';
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  const street = stripHouseNumber(parts[0]);
  const city = (parts[1] ?? '').replace(STATE_ZIP_RE, '').trim();
  const out = [street, city].filter(Boolean).join(', ');
  return out.length > MAX_LABEL_LEN ? `${out.slice(0, MAX_LABEL_LEN - 1)}…` : out;
}
